import { UseMutationOptions, useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError, AxiosResponse } from 'axios';

import {
  HotelPagedResponseDTO,
  HotelFilters,
  ImageTagResponseDto,
  SystemMappingDTO,
  HotelDefaultProvidersDTO,
  UpdateHotelLocationRequestDTO,
  HotelCategoryResponseDTO,
  HotelResponseDTO,
  HotelAmenityPagedResponseDTO,
  HotelAmentityDTO,
  HotelMediaDTO,
  HotelDescriptionsRequestDTO,
  GenericGetFilterDto,
  PagedResponse,
  HotelCategoryDTO,
  HotelCategoryPagedResponseDTO,
  HotelSupplierDto,
  HotelCheckOptionDTO,
  HotelRatingResponseDto,
} from '@bandido/shared/dtos';
import { HotelStatus } from '@bandido/shared/constants';

import { QueryKeys, SortingOptions } from '@frontend/features/common/types';
import { client } from '@frontend/features/common/services';
import { PaginationProps } from '@frontend/features/common/components/Table';

import { useSnackBar } from '../common/components/SnackBarContext';

interface WithErrorCallback {
  onErrorCallback?: (_e: AxiosError) => void;
}

interface GetAllHotelsProps extends WithErrorCallback {
  pagination: Pick<PaginationProps, 'page' | 'rowsPerPage'>;
  filters: HotelFilters;
  freeText: string;
  sortBy: SortingOptions;
}

interface GetBySupplierProps {
  access_code: string;
  pagination: Pick<PaginationProps, 'page' | 'rowsPerPage'>;
}

// QUERIES
export const useGetSuppliersData = () => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getAllHotelSuppliers],
    queryFn: async (): Promise<HotelSupplierDto[] | null> => {
      const response = await client.get<HotelSupplierDto[]>('hotel/supplier');
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.error'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetCategoriesData = () => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getAllHotelCategories],
    queryFn: async (): Promise<AxiosResponse<HotelCategoryResponseDTO> | null> =>
      client.get<HotelCategoryResponseDTO>('hotel/metadata/categories'),
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.error'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetAllHotelsData = ({ pagination, filters, freeText, sortBy }: GetAllHotelsProps) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getAllHotels, pagination.page, pagination.rowsPerPage, freeText, sortBy, filters],
    queryFn: async (): Promise<AxiosResponse<HotelPagedResponseDTO> | null> =>
      client.get<HotelPagedResponseDTO>('hotel/content', {
        params: {
          pageSize: Number(pagination.rowsPerPage) || 0,
          currentPage: Number(pagination.page) || 0,
          ...filters,
          ...sortBy,
          freeText,
        },
      }),
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.error'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetHotelCardData = ({
  onComplete,
  hotelId,
}: {
  hotelId: string;
  onComplete?: (data: AxiosResponse<HotelResponseDTO, any>) => void;
}) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelCardData, hotelId],
    queryFn: async (): Promise<HotelResponseDTO | null> => {
      const response = await client.get<HotelResponseDTO>(`hotel/content/${hotelId}/card-data`);
      onComplete?.(response);
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.detail.error.detailInfo'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
    enabled: !!hotelId,
  });
};

export const useGetHotelData = ({
  onComplete,
  hotelId,
}: {
  hotelId: string;
  onComplete?: (data: AxiosResponse<HotelResponseDTO, any>) => void;
}) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelData, hotelId],
    queryFn: async (): Promise<HotelResponseDTO | null> => {
      const response = await client.get<HotelResponseDTO>(`hotel/content/${hotelId}`);
      onComplete?.(response);
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.detail.error.detailInfo'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
    enabled: !!hotelId,
  });
};

export const useGetHotelRating = ({
  onComplete,
  hotelId,
}: {
  hotelId: string;
  onComplete?: (data: AxiosResponse<HotelRatingResponseDto, any>) => void;
}) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelRating, hotelId],
    queryFn: async (): Promise<HotelRatingResponseDto | null> => {
      const response = await client.get<HotelRatingResponseDto>(`hotel-rating/${hotelId}`);
      onComplete?.(response);
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.rating.error'),
          color: 'warning',
        }),
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(hotelId),
  });
};

export const useGetAllHotelImageTags = ({ currentPage, pageSize, search }: GenericGetFilterDto) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getAllHotelImageTags, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<ImageTagResponseDto> | undefined> => {
      const users: AxiosResponse<PagedResponse<ImageTagResponseDto>, any> = await client.get('hotel/image-tag', {
        params: {
          currentPage,
          pageSize,
          search,
        },
      });
      return users.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.detail.error.detailInfo'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: true,
  });
};

export const useGetHotelImageTagById = (id: string) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  return useQuery({
    queryKey: [QueryKeys.getHotelImageTagById, id],
    queryFn: async (): Promise<ImageTagResponseDto | undefined> => {
      const result: AxiosResponse<ImageTagResponseDto, any> = await client.get(`hotel/image-tag/${id}`);
      return result.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.configuration.defaultValues.getError'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });
};

export const useGetAmenitiesBySuppliers = ({ access_code, pagination }: GetBySupplierProps) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getAmenititesBySupplier, access_code, pagination],
    queryFn: async () => {
      const { data } = await client.get<HotelAmenityPagedResponseDTO>(`hotel-amenity/supplier/${access_code}`, {
        params: { ...pagination },
      });
      return data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.error'),
          color: 'error',
        }),
    },
    enabled: !!access_code,
    refetchOnWindowFocus: false,
  });
};

export const useMappedAmenitiesByDefaultProvider = () => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getAllMappedAmenitites],
    queryFn: async () => {
      const { data } = await client.get<HotelAmentityDTO[]>('hotel-amenity/mapped');
      return data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.error'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
  });
};

export const useMappedCategoriesByDefaultProvider = () => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getAllMappedHotelCategories],
    queryFn: async () => {
      const { data } = await client.get<HotelCategoryDTO[]>('hotel/metadata/category/mapped');
      return data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.error'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetCategoriesBySuppliers = ({ access_code, pagination }: GetBySupplierProps) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getCategoriesBySupplier, access_code, pagination],
    queryFn: async () => {
      const { data } = await client.get<HotelCategoryPagedResponseDTO>(
        `hotel/metadata/category/supplier/${access_code}`,
        {
          params: { ...pagination },
        },
      );
      return data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.error'),
          color: 'error',
        }),
    },
    enabled: !!access_code,
    refetchOnWindowFocus: false,
  });
};

export const useGetDefaultProviders = () => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getDefaultProviders],
    queryFn: async (): Promise<AxiosResponse<HotelDefaultProvidersDTO> | null> =>
      client.get<HotelDefaultProvidersDTO>('hotel/defaults'),
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.configuration.defaultValues.getError'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetHotelModeratedBy = (id?: number) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelModeratedBy, id],
    queryFn: async (): Promise<HotelResponseDTO[]> => {
      const response = await client.get<HotelResponseDTO[]>(`hotel/content/${id}/moderated`);
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.detail.error.detailInfo'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
    enabled: !!id,
  });
};

export const useGetHotelDupesTentative = (id?: number) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelDupesTentative, id],
    queryFn: async (): Promise<AxiosResponse<HotelResponseDTO[]> | null> =>
      client.get<HotelResponseDTO[]>(`hotel/content/dupes/tentatives/${id}`),
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.detail.error.detailInfo'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
    enabled: !!id,
  });
};

export const useGetHotelDupesManual = ({ freeText }: { freeText: string }) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelDupesTentative, freeText],
    queryFn: async (): Promise<AxiosResponse<HotelResponseDTO[]> | null> =>
      client.get<HotelResponseDTO[]>(`hotel/content/dupes/manual/${freeText}`),
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.error'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(freeText.trim()),
  });
};

export const useGetAvailableCountries = () => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getAvailableCountries],
    queryFn: async (): Promise<string[]> => {
      const response = await client.get<string[]>('hotel/content/countries/list');
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.error'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
  });
};

// MUTATIONS

const updateHotelAmenity = async (id: string, data: SystemMappingDTO) =>
  client.put<HotelAmentityDTO>(`hotel-amenity/${id}`, data);

export const useUpdateHotelAmenityMutation = (
  options?:
    | Omit<
        UseMutationOptions<AxiosResponse<HotelAmentityDTO, any>, unknown, { id: string } & SystemMappingDTO, unknown>,
        'mutationFn'
      >
    | undefined,
) =>
  useMutation((data) => {
    const { id, ...rest } = data;
    return updateHotelAmenity(id, rest);
  }, options);

const updateHotelCategory = async (id: string, data: SystemMappingDTO) =>
  client.put<HotelCategoryDTO>(`hotel/metadata/category/${id}`, data);

export const useUpdateHotelCategoryMutation = (
  options?:
    | Omit<
        UseMutationOptions<AxiosResponse<HotelCategoryDTO, any>, unknown, { id: string } & SystemMappingDTO, unknown>,
        'mutationFn'
      >
    | undefined,
) =>
  useMutation((data) => {
    const { id, ...rest } = data;
    return updateHotelCategory(id, rest);
  }, options);

const updateDefaultProviders = async (data: HotelDefaultProvidersDTO) =>
  client.put<HotelDefaultProvidersDTO>('hotel/defaults', data);

export const useUpdateDefaultProvidersMutation = (
  options?:
    | Omit<
        UseMutationOptions<AxiosResponse<HotelDefaultProvidersDTO, any>, unknown, HotelDefaultProvidersDTO, unknown>,
        'mutationFn'
      >
    | undefined,
) => useMutation((data) => updateDefaultProviders(data), options);

interface linkHotelDTO {
  modParentId: number;
  hotelId: number;
}
const linkHotel = async ({ modParentId, hotelId }: linkHotelDTO) =>
  client.put<boolean>(`hotel/content/${modParentId}/link/${hotelId}`);

export const useLinkHotelMutation = (
  options?:
    | Omit<UseMutationOptions<AxiosResponse<boolean, any>, unknown, linkHotelDTO, unknown>, 'mutationFn'>
    | undefined,
) => useMutation((data) => linkHotel(data), options);

interface unlinkHotelDTO {
  hotelId: number;
}
const unlinkHotel = async ({ hotelId }: unlinkHotelDTO) => client.put<boolean>(`hotel/content/${hotelId}/unlink`);

export const useUnlinkHotelMutation = (
  options?:
    | Omit<UseMutationOptions<AxiosResponse<boolean, any>, unknown, unlinkHotelDTO, unknown>, 'mutationFn'>
    | undefined,
) => useMutation((data) => unlinkHotel(data), options);

const updateHotelDescriptions = async ({ hotelId, ...data }: HotelDescriptionsRequestDTO & { hotelId: number }) =>
  client.put<boolean>(`hotel/content/${hotelId}/descriptions`, data);

export const useUpdateHotelDescriptionsMutation = (
  options?:
    | Omit<
        UseMutationOptions<
          AxiosResponse<boolean, any>,
          unknown,
          HotelDescriptionsRequestDTO & { hotelId: number },
          unknown
        >,
        'mutationFn'
      >
    | undefined,
) => useMutation((data) => updateHotelDescriptions(data), options);

const updateHotelLocation = async ({ hotelId, ...data }: UpdateHotelLocationRequestDTO & { hotelId: number }) =>
  client.put<boolean>(`hotel/content/${hotelId}/location`, data);

export const useUpdateHotelLocationMutation = (
  options?:
    | Omit<
        UseMutationOptions<
          AxiosResponse<boolean, any>,
          unknown,
          UpdateHotelLocationRequestDTO & { hotelId: number },
          unknown
        >,
        'mutationFn'
      >
    | undefined,
) => useMutation((data) => updateHotelLocation(data), options);

const updateHotelImages = async ({ hotelId, images }: { hotelId: number; images: HotelMediaDTO[] }) =>
  client.put<boolean>(`hotel/content/${hotelId}/images`, images);

export const useUpdateHotelImagesMutation = (
  options?:
    | Omit<
        UseMutationOptions<AxiosResponse<boolean, any>, unknown, { hotelId: number; images: HotelMediaDTO[] }, unknown>,
        'mutationFn'
      >
    | undefined,
) => useMutation((data) => updateHotelImages(data), options);

const updateHotelAmenities = async ({ hotelId, ids }: { hotelId: string; ids: number[] }) =>
  client.put<boolean>(`hotel/content/${hotelId}/amenities`, { ids });

export const useUpdateHotelAmenitiesMutation = (
  options?:
    | Omit<
        UseMutationOptions<AxiosResponse<boolean, any>, unknown, { hotelId: string; ids: number[] }, unknown>,
        'mutationFn'
      >
    | undefined,
) => useMutation((data) => updateHotelAmenities(data), options);

const updateHotelStatus = async (params: { id: number; status: HotelStatus }) =>
  client.put<boolean>(`hotel/content/${params.id}/status/${params.status}`);

export const useUpdateHotelStatus = (
  options?:
    | Omit<
        UseMutationOptions<AxiosResponse<boolean, any>, unknown, { id: number; status: HotelStatus }, unknown>,
        'mutationFn'
      >
    | undefined,
) => useMutation((params) => updateHotelStatus(params), options);

const updateHotelCheckOptions = async ({
  hotelId,
  checkOptions,
}: {
  hotelId: string;
  checkOptions: Partial<HotelCheckOptionDTO>[];
}) => client.put<boolean>(`hotel/content/${hotelId}/check-options`, checkOptions);

export const useUpdateHotelCheckOptions = (
  options?:
    | Omit<
        UseMutationOptions<
          AxiosResponse<boolean, any>,
          unknown,
          { hotelId: string; checkOptions: Partial<HotelCheckOptionDTO>[] },
          unknown
        >,
        'mutationFn'
      >
    | undefined,
) => useMutation((data) => updateHotelCheckOptions(data), options);
